import styled from "@emotion/styled";
import { Typography } from "@kaltura/ds-react-components";
// noinspection ES6PreferShortImport
import { EventsListItemTags } from "../event-list-item-tags/EventsListItemTags";
import { Truncate } from "@kaltura/mediaspace-shared-ui";
import ActionSection from "./action-section/ActionSection";
import EventPresenterLargeList from "../event-presenter/event-presenter-large-list/EventPresenterLargeList";
import { Breakpoint } from "@mui/material";

/**
 * Container element of the whole event list item card.
 */
export const EventListItemContainer = styled("div")(({ theme }) => ({
    borderRadius: theme.kaltura.shape.roundness2,
    backgroundColor: theme.kaltura.palette.surfaces.paper,
}));

/**
 * Flex container of card items that go horizontally on desktop.
 */
export const EventListItemRowContainer = styled("div")<{ verticalBreakpoint?: Breakpoint | number }>(
    ({ theme, verticalBreakpoint = "md" }) => ({
        display: "flex",
        padding: theme.spacing(2.5, 3, 2),
        gap: theme.spacing(3),
        [theme.breakpoints.down(verticalBreakpoint)]: {
            display: "block",
        },
    })
);

export const SchedulingContainer = styled("div")<{ vertical?: boolean }>(({ theme, vertical }) => [
    {
        flex: "1 1 0",
    },
    vertical
        ? {
              maxWidth: 180,
              marginTop: 3,
          }
        : {
              display: "flex",
              flexDirection: "row-reverse",
              justifyContent: "flex-end",
              alignItems: "start",
              gap: theme.spacing(1),
          },
]);

export const SchedulingLine = styled(
    Typography,
    {
        shouldForwardProp(propName: PropertyKey): boolean {
            return propName !== "content";
        },
    }
)<{ content: "time" | "date"; vertical?: boolean }>(({ theme, content, vertical }) => [
    {
        color: theme.kaltura.palette.tone2,
        display: "block",
        paddingBottom: theme.spacing(1),
    },
    !vertical && {
        display: "inline",
        order: content === "time" ? 2 : 3,
    },
]);

/**
 * Container element for the main part of the card (title, description, presenters, etc.)
 */
export const EventListItemContentContainer = styled("div")(({ theme }) => ({
    flex: "2 2 0",
    // add minimal width value in order for flex to operate correctly
    width: 0,
    backgroundColor: theme.kaltura.palette.surfaces.paper,
    borderTopLeftRadius: theme.kaltura.shape.roundness1,
    borderBottomLeftRadius: theme.kaltura.shape.roundness1,
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        width: "auto",
    },
}));

export const EntryLink = styled("a")<{ disabled?: boolean }>(({ disabled, theme }) => ({
    ...(
        disabled && {
            pointerEvents: "none",
        }
    ),
}));

export const Name = styled(Typography)(({ theme }) => ({
    color: theme.kaltura.palette.tone1,
    margin: 0,
    "& a": {
        textDecoration: "none",
        "&:hover, &:focus": {
            color: theme.kaltura.palette.tone1,
        },
    },
}));

export const Tags = styled(EventsListItemTags)(({ theme }) => ({
    padding: 0,
    marginTop: theme.spacing(1),
}));

export const Description = styled(Truncate)(({ theme, lines = 0 }) => ({
    color: theme.kaltura.palette.tone2,
    fontSize: theme.typography.pxToRem(14),
    marginTop: theme.spacing(1),
    "&.truncation-error": {
        maxHeight: `${lines * 1.5}em`,
        overflow: "hidden",
    },
    "& a": {
        fontWeight: theme.kaltura.typography.fontWeightBold,
        textDecoration: "none",
    },
}));

export const Presenters = styled(
    EventPresenterLargeList,
    { shouldForwardProp: (propName) => propName !== "dense" }
)<{ dense?: boolean }>(({ theme, dense }) => ({
    marginTop: theme.spacing(dense ? 2 : 3),
}));

export const Thumbnail = styled("img")(({ theme }) => ({
    width: 224,
    height: 126,
    borderRadius: theme.kaltura.shape.roundness2,
}));

export const StyledActionSection = styled(ActionSection)(({ theme }) => ({
    borderTop: `1px solid ${theme.kaltura.palette.tone4}`,
    padding: theme.spacing(3),
}));

export const ButtonTextWrapper = styled("span")(({ theme }) => ({
    [theme.breakpoints.between(theme.breakpoints.values.sm, theme.breakpoints.values.md)]: {
        maxWidth: 130,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "inline-block",
    },
}));
